// src/services/axios-latest-only.js
import axios from "axios";

// Keep only the latest pending GET per pathname (params don't matter).
// Uses a per-request token so old aborts don't clear the new controller.
const byPath = new Map(); // key -> { controller, token }

function pathKey(url) {
  try {
    const u = new URL(url, window.location.origin);
    return u.pathname.replace(/\/+$/, "");
  } catch {
    return String(url).split("?")[0].replace(/\/+$/, "");
  }
}

function fullUrl(cfg) {
  try {
    return new URL(cfg?.url || "", cfg?.baseURL || window.location.origin).toString();
  } catch {
    return cfg?.url || "";
  }
}

function mergeSignals(a, b) {
  if (!a && !b) return undefined;
  if (a && !b) return a;
  if (!a && b) return b;
  const ctrl = new AbortController();
  const onAbort = () => ctrl.abort();
  a.addEventListener("abort", onAbort, { once: true });
  b.addEventListener("abort", onAbort, { once: true });
  return ctrl.signal;
}

axios.interceptors.request.use((cfg) => {
  const method = (cfg.method || "get").toUpperCase();
  if (method !== "GET" || !cfg.url) return cfg;

  const key = pathKey(fullUrl(cfg));

  // cancel previous pending request for this path
  const prev = byPath.get(key);
  if (prev && !prev.controller.signal.aborted) {
    prev.controller.abort("Cancelled by newer request for same path");
  }

  // create and track the new one WITH a token
  const controller = new AbortController();
  const token = Symbol(key);
  cfg.signal = mergeSignals(cfg.signal, controller.signal);
  cfg.__latestOnly = { key, token };           // stash on config

  byPath.set(key, { controller, token });
  return cfg;
});

axios.interceptors.response.use(
  (res) => {
    const info = res?.config?.__latestOnly;
    if (!info) return res;

    const { key, token } = info;
    const tracked = byPath.get(key);

    // only clear if THIS response belongs to the currently tracked request
    if (tracked && tracked.token === token) byPath.delete(key);
    return res;
  },
  (err) => {
    const cfg = err?.config;
    const info = cfg?.__latestOnly;
    if (info) {
      const { key, token } = info;
      const tracked = byPath.get(key);
      if (tracked && tracked.token === token) byPath.delete(key);
    }
    return Promise.reject(err);
  }
);
